import { useEffect, useRef, useState } from 'react';
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import YouTubePlayer, { type YouTubePlayerHandle } from '@/components/YouTubePlayer';
import ReviewVideoTransportBar from '@/components/review/ReviewVideoTransportBar';
import { formatVideoTime } from '@/types';
import { proModeSurfaces } from '@/utils/themeSurfaces';

interface Props {
  open: boolean;
  onClose: () => void;
  videoId: string | null | undefined;
  initialSeconds: number;
  videoDuration?: number;
  title?: string;
  subtitle?: string;
}

export default function VideoTimestampPreviewDialog({
  open,
  onClose,
  videoId,
  initialSeconds,
  videoDuration = 0,
  title = '影片片段',
  subtitle,
}: Props) {
  const theme = useTheme();
  const surfaces = proModeSurfaces(theme);
  const playerRef = useRef<YouTubePlayerHandle>(null);
  const [currentTime, setCurrentTime] = useState(initialSeconds);
  const [duration, setDuration] = useState(videoDuration);
  const [isPlaying, setIsPlaying] = useState(false);
  const [playbackRate, setPlaybackRate] = useState(1);

  useEffect(() => {
    if (!open) return;
    setCurrentTime(initialSeconds);
    setPlaybackRate(1);
    setIsPlaying(false);
    const id = window.setTimeout(() => {
      playerRef.current?.seekTo(initialSeconds);
      playerRef.current?.play();
    }, 150);
    return () => window.clearTimeout(id);
  }, [open, initialSeconds]);

  useEffect(() => {
    if (videoDuration > 0) setDuration(videoDuration);
  }, [videoDuration]);

  const seek = (seconds: number) => {
    const max = duration > 0 ? duration : undefined;
    const clamped = max != null ? Math.min(Math.max(0, seconds), max) : Math.max(0, seconds);
    setCurrentTime(clamped);
    playerRef.current?.seekTo(clamped);
  };

  const handlePlaybackRateChange = (rate: number) => {
    setPlaybackRate(rate);
    playerRef.current?.setPlaybackRate(rate);
  };

  const replayFromMark = () => {
    seek(initialSeconds);
    playerRef.current?.play();
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle sx={{ pr: 6 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h6" fontWeight={700}>
              {title}
            </Typography>
            {subtitle ? (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                {subtitle}
              </Typography>
            ) : (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mt: 0.25, fontVariantNumeric: 'tabular-nums' }}
              >
                {formatVideoTime(initialSeconds)}
              </Typography>
            )}
          </Box>
          <IconButton aria-label="關閉" onClick={onClose} sx={{ position: 'absolute', right: 12, top: 12 }}>
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent
        sx={{
          p: 0,
          display: 'flex',
          flexDirection: 'column',
          bgcolor: surfaces.timelineBg,
        }}
      >
        <Box
          sx={{
            position: 'relative',
            width: '100%',
            aspectRatio: '16 / 9',
            bgcolor: '#000',
            flexShrink: 0,
            borderBottom: '1px solid',
            borderColor: surfaces.border,
          }}
        >
          <YouTubePlayer
            ref={playerRef}
            videoId={videoId}
            initialTime={initialSeconds}
            playbackRate={playbackRate}
            fillContainer
            hideControls
            onTimeUpdate={setCurrentTime}
            onDurationReady={setDuration}
            onPlayingChange={setIsPlaying}
          />
        </Box>
        <ReviewVideoTransportBar
          currentTime={currentTime}
          videoDuration={duration}
          playbackRate={playbackRate}
          isPlaying={isPlaying}
          onTogglePlay={() => playerRef.current?.togglePlay()}
          onSeek={seek}
          onPlaybackRateChange={handlePlaybackRateChange}
          onReplay={replayFromMark}
          replayAtSeconds={initialSeconds}
        />
      </DialogContent>
    </Dialog>
  );
}
